import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import {
    Collapse,
    Divider,
    Grid,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { addDays, format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import {
    useCalculatePriceMutation,
    useGetFeeListQuery,
    useGetPlatformSettingsQuery,
} from '../../../../app/Slices/SevenDayApiSlice';
import {
    capitalizeName,
    formatCurrency,
    groupCleaningFees,
    sum,
} from '../../../../shared/utils';

import { useFormContext, useWatch } from 'react-hook-form';
import UserRole from '../../../../shared/AuthRoles';
import { ExperienceCharge } from '../../../types/experience';
import { getCleaningFeeText } from '../Owner/Pricing/CleaningFeeCalendarView';
import { generateSpaceNames } from '../Spaces/SpaceConfiguration';
import { useFormState } from '../Form/useFormState';

export default function NetEarnings() {
    const { experience } = useFormState();

    const [calculatePrice] = useCalculatePriceMutation();
    const [showCleaningFeeDetails, setShowCleaningFeeDetails] = useState(false);
    const { control } = useFormContext();
    const loggedInUser = UserRole();
    const theme = useTheme();

    const charges = useWatch({ control, name: 'charges' });
    const minHireHours = useWatch({ control, name: 'minHireHours' });
    const bedRooms = useWatch({ control, name: 'details.bedrooms' });
    const privateOffices = useWatch({
        control,
        name: 'details.privateOffices',
    });
    const meetingRooms = useWatch({ control, name: 'details.meetingRooms' });
    const experienceSpaces = useWatch({ control, name: 'spaces' });

    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');
    const { data: getPlatformSettings } =
        useGetPlatformSettingsQuery('/platform-settings');

    const { data: availableFees = [] } = useGetFeeListQuery(
        loggedInUser?.company,
    );

    const findBasePrice = charges.filter((fee: any) => {
        return fee?.chargeType === 'price';
    });

    const findDayPrice = charges.filter((price: ExperienceCharge) => {
        return price.chargeType === 'flexiblePrice';
    });
    const cleaningFee = charges.filter((price: ExperienceCharge) => {
        return price.chargeType === 'cleaningFee';
    });

    const [cleaningFeeData, setCleaningFeeData] = useState<any>();
    const [, setCleaningFeeError] = useState<any>();

    const spaces = generateSpaceNames(experienceSpaces).map((space: any) => ({
        ...space,
        id: space?.id || space?.demo_id,
    }));

    useEffect(() => {
        const fetchData = async () => {
            try {
                if (!cleaningFee?.length) {
                    setCleaningFeeData(undefined);
                    setCleaningFeeError(false);
                    return;
                }

                const startDate = format(new Date(), 'yyyy-MM-dd');

                const endDate = format(addDays(new Date(), 2), 'yyyy-MM-dd');

                const request = {
                    charges: cleaningFee,
                    startDate,
                    endDate,
                    checkinTime: '08:00',
                    checkoutTime: '17:00',
                    module: {
                        bedRooms: bedRooms ?? 1,
                        privateOffices: privateOffices ?? 1,
                        meetingRooms: meetingRooms ?? 1,
                        spaces,
                    },
                    overnightGuests: 3,
                    spaces: spaces.map((space: any) => ({
                        startDate,
                        endDate,
                        id: space?.id || space?.demo_id,
                        attendees: [],
                    })),
                    entireVenue: true,
                    privateOffices: 0,
                    meetingRooms: 0,
                    minHireHours,
                    type: experience.type,
                };

                const response = await calculatePrice([
                    loggedInUser?.company,
                    request,
                ]).unwrap();

                setCleaningFeeData(response);
                setCleaningFeeError(false);
            } catch (_error) {
                setCleaningFeeError(true);
            }
        };
        fetchData();
    }, [charges]);

    const cleaningFeeForPreview = cleaningFeeData?.charges
        ? groupCleaningFees(cleaningFeeData?.charges)
        : [];

    const dayPrice = findDayPrice?.[0]?.dayPrice ?? 0;
    const dayWeekendPrice = findDayPrice?.[0]?.weekendPrice?.dayPrice ?? 0;

    const findType = findBasePrice?.[0]?.type;

    const basePrice =
        findBasePrice?.length > 0
            ? experience.type === 'commercial'
                ? findBasePrice.find(
                      (price: any) =>
                          price.type === 'entireVenuePerDay' ||
                          price.type === 'entireVenuePerNight',
                  )?.unitPrice
                : findBasePrice?.[0]?.unitPrice
            : findDayPrice?.length > 0
              ? dayPrice
              : 0;

    const weekendPrice = findBasePrice?.[0]?.weekendPrice
        ? findBasePrice?.[0]?.weekendPrice?.unitPrice
        : findDayPrice?.[0]?.weekendPrice
          ? dayWeekendPrice
          : 0;

    const getFees = charges.filter((fee: any) => {
        return fee?.chargeType === 'fee';
    });

    const fees = getFees?.map((fee: any) => {
        return {
            feeId: fee.feeId,
            feeName:
                fee?.name ??
                availableFees.find((f: any) => f.id === fee.feeId).name,
            feePrice: fee?.unitPrice,
        };
    });

    const feeAmounts = getFees?.map((fee: any) => {
        return fee?.unitPrice;
    });

    const feeTotals = feeAmounts?.length > 0 ? sum(feeAmounts) : 0;
    // const feeTotals = 0

    const serviceFee = getPlatformSettings?.hostServiceFeePercentage;
    // Base price calcs
    const baseTallyTotalsExcludingGST =
        feeTotals + basePrice + (cleaningFeeForPreview[0]?.total || 0);
    const GSTBase = (baseTallyTotalsExcludingGST * 10) / 100;
    const baseTallyTotalsIncGST = baseTallyTotalsExcludingGST + GSTBase;

    const lessServiceFeeBase = (baseTallyTotalsIncGST * serviceFee) / 100;
    const hostNetpayBase = baseTallyTotalsIncGST - lessServiceFeeBase;

    // Weekend price calcs
    const weekendTallyTotalsExcludingGST = Number(
        feeTotals + weekendPrice + (cleaningFeeForPreview[0]?.total || 0),
    );
    const GSTWeekend = (weekendTallyTotalsExcludingGST * 10) / 100;
    const weekendTallyTotalsIncGST = Number(
        weekendTallyTotalsExcludingGST + GSTWeekend,
    );

    const lessServiceFeeWeekend = (weekendTallyTotalsIncGST * serviceFee) / 100;
    const hostNetpayWeekend = weekendTallyTotalsIncGST - lessServiceFeeWeekend;

    const basePriceEarnings = {
        gst: GSTBase,
        totalIncGst: baseTallyTotalsIncGST,
        serviceFee: lessServiceFeeBase,
        netPay: hostNetpayBase,
    };

    const weekendPriceEarnings = {
        gst: GSTWeekend,
        totalIncGst: weekendTallyTotalsIncGST,
        serviceFee: lessServiceFeeWeekend,
        netPay: hostNetpayWeekend,
    };

    cleaningFeeForPreview[0]?.details.map((charge: any) => {
        const text = getCleaningFeeText(charge, spaces);
        return text;
    });

    const CleaningFeeDetails = () => {
        return (
            <Grid>
                <Grid
                    sx={{
                        display: 'flex',
                    }}
                >
                    {' '}
                    <Typography
                        sx={{
                            color: 'primary',
                            fontSize: '14px',
                            width: '100%',
                            paddingBottom: '5px',
                        }}
                    >
                        Cleaning Fee{' '}
                        {cleaningFeeForPreview[0]?.cleaningFeeType !==
                            'flat' && (
                            <IconButton
                                size='small'
                                onClick={() =>
                                    setShowCleaningFeeDetails((prev) => !prev)
                                }
                                sx={{
                                    textTransform: 'none',
                                    padding: 0,
                                }}
                            >
                                {showCleaningFeeDetails ? (
                                    <KeyboardArrowUp
                                        sx={{ fontSize: '16px' }}
                                    />
                                ) : (
                                    <KeyboardArrowDown
                                        sx={{ fontSize: '16px' }}
                                    />
                                )}
                            </IconButton>
                        )}
                    </Typography>
                    <Typography
                        sx={{
                            color: 'primary',
                            fontSize: '14px',
                            width: '100%',
                            textAlign: 'right',
                        }}
                    >
                        {formatCurrency(cleaningFeeForPreview[0]?.total)}
                    </Typography>
                </Grid>

                <Collapse in={showCleaningFeeDetails}>
                    {cleaningFeeForPreview[0]?.details
                        ?.filter((charge: any) => charge.total > 0)
                        .map((charge: any) => (
                            <Grid
                                key={`${charge.chargeType}-${charge.type}`}
                                sx={{
                                    display: 'flex',
                                    paddingLeft: 1,
                                    justifyContent: 'space-between',
                                    width: '80%',
                                    maxWidth: '350px',
                                }}
                            >
                                {' '}
                                <Typography
                                    sx={{
                                        color: 'primary',
                                        fontSize: '12px',

                                        paddingBottom: '5px',
                                    }}
                                >
                                    {`${
                                        getCleaningFeeText(charge, spaces)
                                            .description
                                    }`}
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'primary',
                                        fontSize: '12px',
                                        paddingBottom: '5px',
                                    }}
                                >
                                    {`${
                                        getCleaningFeeText(charge, spaces)
                                            .amount
                                    }`}
                                </Typography>
                            </Grid>
                        ))}
                </Collapse>
            </Grid>
        );
    };

    return (
        <>
            {charges.length > 0 && (
                <>
                    <div>
                        <Typography variant='h3'>Your Earnings</Typography>
                        <Typography
                            variant='body2'
                            sx={{ color: 'text.secondary' }}
                        >
                            An example of your net payment minus our 7DAY
                            Service Fee
                            {experience.type === 'commercial'
                                ? ' for the entire property.'
                                : '.'}
                        </Typography>
                        <Typography
                            variant='body3'
                            sx={{ color: 'text.secondary' }}
                        >
                            Note: Amounts do not include any active discounts.
                        </Typography>
                    </div>
                    <Grid
                        sx={{
                            paddingTop: '20px',
                            width: '100%',
                        }}
                    >
                        {charges?.[0]?.weekendPrice === null ? (
                            <Grid
                                sx={{
                                    width: '100%',
                                    maxWidth:
                                        isMobile || isIpad ? '100%' : '400px',
                                }}
                            >
                                <Grid
                                    sx={{
                                        display: 'flex',
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            color: 'primary',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            width: '100%',
                                            paddingBottom: '5px',
                                        }}
                                    >
                                        {experience.type === 'commercial'
                                            ? 'Entire Property '
                                            : 'Base Price '}

                                        {findType === 'perHour'
                                            ? '(per hour)'
                                            : findType === 'perDay'
                                              ? '(per day)'
                                              : findType === 'perNight'
                                                ? '(per night)'
                                                : findType === 'perDayPerGuest'
                                                  ? '(per day per guest)'
                                                  : findType ===
                                                      'perNightPerGuest'
                                                    ? '(per night per guest)'
                                                    : findType ===
                                                        'perHourPerGuest'
                                                      ? '(per hour per guest)'
                                                      : ''}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            color: 'primary',
                                            fontSize: '14px',
                                            width: '100%',
                                            textAlign: 'right',
                                        }}
                                    >
                                        {formatCurrency(basePrice)}
                                    </Typography>
                                </Grid>

                                {fees?.map((item: any) => (
                                    <Grid
                                        key={item.feeId}
                                        sx={{
                                            display: 'flex',
                                        }}
                                    >
                                        {' '}
                                        <Typography
                                            sx={{
                                                color: 'primary',
                                                fontSize: '14px',
                                                width: '100%',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            {capitalizeName(item?.feeName)}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: 'primary',
                                                fontSize: '14px',
                                                width: '100%',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {formatCurrency(item?.feePrice)}
                                        </Typography>
                                    </Grid>
                                ))}

                                {cleaningFeeForPreview?.length > 0 && (
                                    <CleaningFeeDetails />
                                )}

                                <Divider
                                    sx={{
                                        color: theme.palette.faded.main,
                                    }}
                                />

                                {Object.entries(basePriceEarnings).map(
                                    (item: any) => (
                                        <Grid
                                            key={item[0]}
                                            sx={{
                                                display: 'flex',
                                            }}
                                        >
                                            {' '}
                                            <Typography
                                                sx={{
                                                    color: 'primary',
                                                    fontSize: '14px',
                                                    fontWeight:
                                                        item[0] === 'gst' ||
                                                        item[0] ===
                                                            'totalIncGst' ||
                                                        item[0] === 'serviceFee'
                                                            ? ''
                                                            : 'bold',

                                                    width: '100%',
                                                    paddingBottom: '5px',
                                                }}
                                            >
                                                {capitalizeName(
                                                    item?.[0] === 'gst'
                                                        ? 'GST 10%'
                                                        : item?.[0] ===
                                                            'totalIncGst'
                                                          ? 'Total (Inc GST)'
                                                          : item?.[0] ===
                                                              'serviceFee'
                                                            ? `less 7DAY Service Fee ${serviceFee}%`
                                                            : item?.[0] ===
                                                                'netPay'
                                                              ? 'Your net pay'
                                                              : item?.[0],
                                                )}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: 'primary',
                                                    fontSize: '14px',
                                                    fontWeight:
                                                        item[0] === 'netPay'
                                                            ? 'bold'
                                                            : '',
                                                    width: '100%',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {item?.[0] === 'serviceFee'
                                                    ? `(${formatCurrency(
                                                          item?.[1],
                                                      )})`
                                                    : `${formatCurrency(
                                                          item?.[1],
                                                      )}`}
                                            </Typography>
                                        </Grid>
                                    ),
                                )}
                            </Grid>
                        ) : (
                            <Grid
                                sx={{
                                    display: 'flex',
                                    flexDirection:
                                        isMobile || isIpad ? 'column' : 'row',
                                    width: '100%',
                                    gap: isMobile || isIpad ? '0' : '100px',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Grid
                                    sx={{
                                        width: '100%',
                                        maxWidth:
                                            isMobile || isIpad
                                                ? '100%'
                                                : '400px',
                                    }}
                                >
                                    {' '}
                                    <Grid
                                        sx={{
                                            display: 'flex',
                                        }}
                                    >
                                        {' '}
                                        <Typography
                                            sx={{
                                                color: 'primary',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                width: '100%',
                                                paddingBottom: '5px',
                                            }}
                                        >
                                            {experience.type === 'commercial'
                                                ? 'Entire Property'
                                                : 'Base Price '}
                                            {findType === 'perHour'
                                                ? '(per hour)'
                                                : findType === 'perDay'
                                                  ? '(per day)'
                                                  : findType === 'perNight'
                                                    ? '(per night)'
                                                    : findType ===
                                                        'perDayPerGuest'
                                                      ? '(per day per guest)'
                                                      : findType ===
                                                          'perNightPerGuest'
                                                        ? '(per night per guest)'
                                                        : findType ===
                                                            'perHourPerGuest'
                                                          ? '(per hour per guest)'
                                                          : ''}
                                        </Typography>
                                        <Typography
                                            sx={{
                                                color: 'primary',
                                                fontSize: '14px',
                                                width: '100%',
                                                textAlign: 'right',
                                            }}
                                        >
                                            {formatCurrency(basePrice)}
                                        </Typography>
                                    </Grid>
                                    {fees.map((item: any) => (
                                        <Grid
                                            key={item.feeId}
                                            sx={{
                                                display: 'flex',
                                            }}
                                        >
                                            {' '}
                                            <Typography
                                                sx={{
                                                    color: 'primary',
                                                    fontSize: '14px',
                                                    width: '100%',
                                                    paddingBottom: '5px',
                                                }}
                                            >
                                                {capitalizeName(item?.feeName)}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: 'primary',
                                                    fontSize: '14px',
                                                    width: '100%',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatCurrency(item?.feePrice)}
                                            </Typography>
                                        </Grid>
                                    ))}
                                    {cleaningFeeForPreview?.length > 0 && (
                                        <CleaningFeeDetails />
                                    )}
                                    <Divider
                                        sx={{
                                            color: theme.palette.faded.main,
                                        }}
                                    />
                                    {Object.entries(basePriceEarnings).map(
                                        (item: any) => (
                                            <Grid
                                                key={item[0]}
                                                sx={{
                                                    display: 'flex',
                                                }}
                                            >
                                                {' '}
                                                <Typography
                                                    sx={{
                                                        color: 'primary',
                                                        fontSize: '14px',
                                                        fontWeight:
                                                            item[0] === 'gst' ||
                                                            item[0] ===
                                                                'totalIncGst' ||
                                                            item[0] ===
                                                                'serviceFee'
                                                                ? ''
                                                                : 'bold',

                                                        width: '100%',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    {capitalizeName(
                                                        item?.[0] === 'gst'
                                                            ? 'GST 10%'
                                                            : item?.[0] ===
                                                                'totalIncGst'
                                                              ? 'Total (Inc GST)'
                                                              : item?.[0] ===
                                                                  'serviceFee'
                                                                ? `less 7DAY Service Fee ${serviceFee}%`
                                                                : item?.[0] ===
                                                                    'netPay'
                                                                  ? 'Your net pay'
                                                                  : item?.[0],
                                                    )}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'primary',
                                                        fontSize: '14px',
                                                        fontWeight:
                                                            item[0] === 'netPay'
                                                                ? 'bold'
                                                                : '',
                                                        width: '100%',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {item?.[0] === 'serviceFee'
                                                        ? `(${formatCurrency(
                                                              item?.[1],
                                                          )})`
                                                        : `${formatCurrency(
                                                              item?.[1],
                                                          )}`}
                                                </Typography>
                                            </Grid>
                                        ),
                                    )}
                                </Grid>
                                {experience.type === 'commercial' ||
                                weekendPrice === 0 ? null : (
                                    <Grid
                                        sx={{
                                            paddingTop:
                                                isMobile || isIpad
                                                    ? '25px'
                                                    : '',
                                            width: '100%',
                                            maxWidth:
                                                isMobile || isIpad
                                                    ? '100%'
                                                    : '400px',
                                        }}
                                    >
                                        <Grid
                                            sx={{
                                                display: 'flex',
                                            }}
                                        >
                                            {' '}
                                            <Typography
                                                sx={{
                                                    color: 'primary',
                                                    fontSize: '14px',
                                                    fontWeight: 'bold',
                                                    width: '100%',
                                                    paddingBottom: '5px',
                                                }}
                                            >
                                                Weekend Price
                                                {findType === 'perHour'
                                                    ? ' (per hour)'
                                                    : findType === 'perDay'
                                                      ? ' (per day)'
                                                      : findType === 'perNight'
                                                        ? ' (per night)'
                                                        : findType ===
                                                            'perDayPerGuest'
                                                          ? ' (per day per guest)'
                                                          : findType ===
                                                              'perNightPerGuest'
                                                            ? ' (per night per guest)'
                                                            : findType ===
                                                                'perHourPerGuest'
                                                              ? ' (per hour per guest)'
                                                              : ''}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: 'primary',
                                                    fontSize: '14px',
                                                    width: '100%',
                                                    textAlign: 'right',
                                                }}
                                            >
                                                {formatCurrency(weekendPrice)}
                                            </Typography>
                                        </Grid>
                                        {fees.map((item: any) => (
                                            <Grid
                                                key={item.feeId}
                                                sx={{
                                                    display: 'flex',
                                                }}
                                            >
                                                {' '}
                                                <Typography
                                                    sx={{
                                                        color: 'primary',
                                                        fontSize: '14px',
                                                        width: '100%',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    {capitalizeName(
                                                        item?.feeName,
                                                    )}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'primary',
                                                        fontSize: '14px',
                                                        width: '100%',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {formatCurrency(
                                                        item?.feePrice,
                                                    )}
                                                </Typography>
                                            </Grid>
                                        ))}

                                        {cleaningFeeForPreview?.length > 0 && (
                                            <CleaningFeeDetails />
                                        )}
                                        <Divider
                                            sx={{
                                                color: theme.palette.faded.main,
                                            }}
                                        />
                                        {Object.entries(
                                            weekendPriceEarnings,
                                        ).map((item: any) => (
                                            <Grid
                                                key={item[0]}
                                                sx={{
                                                    display: 'flex',
                                                }}
                                            >
                                                {' '}
                                                <Typography
                                                    sx={{
                                                        color: 'primary',
                                                        fontSize: '14px',
                                                        fontWeight:
                                                            item[0] === 'gst' ||
                                                            item[0] ===
                                                                'totalIncGst' ||
                                                            item[0] ===
                                                                'serviceFee'
                                                                ? ''
                                                                : 'bold',

                                                        width: '100%',
                                                        paddingBottom: '5px',
                                                    }}
                                                >
                                                    {capitalizeName(
                                                        item?.[0] === 'gst'
                                                            ? 'GST 10%'
                                                            : item?.[0] ===
                                                                'totalIncGst'
                                                              ? 'Total (Inc GST)'
                                                              : item?.[0] ===
                                                                  'serviceFee'
                                                                ? `less 7DAY Service Fee ${serviceFee}%`
                                                                : item?.[0] ===
                                                                    'netPay'
                                                                  ? 'Your net pay'
                                                                  : item?.[0],
                                                    )}
                                                </Typography>
                                                <Typography
                                                    sx={{
                                                        color: 'primary',
                                                        fontSize: '14px',
                                                        fontWeight:
                                                            item[0] === 'netPay'
                                                                ? 'bold'
                                                                : '',
                                                        width: '100%',
                                                        textAlign: 'right',
                                                    }}
                                                >
                                                    {item?.[0] === 'serviceFee'
                                                        ? `(${formatCurrency(
                                                              item?.[1],
                                                          )})`
                                                        : `${formatCurrency(
                                                              item?.[1],
                                                          )}`}
                                                </Typography>
                                            </Grid>
                                        ))}
                                    </Grid>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </>
            )}
        </>
    );
}
